/** Web counterpart of ReadOSTranscriptTextSelectionAdapter and the read-only
 * ReadexTextSelectionPopoverContent. Serialized into the opaque frame: keep
 * this function closed and use only the original payload model for identity. */
export function installAnnotationCapsules() {
  const w = window as any;
  let active: { chip: HTMLElement; popup: HTMLElement; signature: string; annotationID?: string; messageID: string } | null = null;
  let closeTimer: ReturnType<typeof setTimeout> | undefined;
  let sequence = 0;
  let restoringFocus = false;
  const bound = new WeakSet<HTMLElement>();
  const trimmed = (value: unknown) => typeof value === 'string' ? value.trim() : '';
  const cancelClose = () => { if (closeTimer !== undefined) clearTimeout(closeTimer); closeTimer = undefined; };
  function close(restoreFocus = false) {
    cancelClose();
    const previous = active; active = null;
    if (!previous) return;
    previous.chip.setAttribute('aria-expanded', 'false');
    previous.chip.removeAttribute('aria-controls');
    if (previous.annotationID) w.ReadexResponseAnnotationRenderer?.setActive?.(previous.annotationID, previous.messageID, false);
    const needsFocus = restoreFocus && previous.popup.contains(document.activeElement);
    previous.popup.remove();
    if (needsFocus) { restoringFocus = true; previous.chip.focus({ preventScroll: true }); restoringFocus = false; }
  }
  function state(chip: HTMLElement) {
    const reference = chip.dataset.readexResponseAnnotation === 'true';
    const messageID = reference ? chip.dataset.messageId : chip.closest<HTMLElement>('[data-message-id]')?.dataset.messageId;
    const model = w.__chatTranscriptPayloadModel;
    const message = messageID && model?.messageByID?.(messageID);
    const available = Array.isArray(message?.responseAnnotations) ? message.responseAnnotations : [];
    const referenceIndex = reference ? Number(chip.dataset.readexAnnotationIndex) : undefined;
    const referenced = referenceIndex && Number.isSafeInteger(referenceIndex) ? available[referenceIndex - 1] : undefined;
    // A rendered marker's ordinal alone is not a source identity. Require its
    // exact message + array position + stored annotation id before opening.
    if (reference && (!referenced || referenced.id !== chip.dataset.readexAnnotationId || !trimmed(referenced.text || referenced.selectedText))) return null;
    const items = reference ? [referenced] : available.filter((item: any) => trimmed(item?.text || item?.selectedText));
    if (!messageID || !items.length) return null;
    const color = items[0].highlightColor || {};
    const rgb = [color.red, color.green, color.blue].map(value => Number.isFinite(Number(value)) ? Math.round(Math.max(0, Math.min(1, Number(value))) * 255) : 0);
    return { messageID, items, referenceIndex, annotationID: reference ? referenced.id as string : undefined, accent: `rgb(${rgb.join(', ')})`,
      border: `rgba(${rgb.join(', ')}, 0.34)`, background: `rgba(${rgb.join(', ')}, 0.13)`,
      signature: JSON.stringify([messageID, items]) };
  }
  function scheduleClose() {
    cancelClose();
    // Cross the small anchor/popover gap without collapsing the reading surface.
    closeTimer = setTimeout(() => {
      if (!active) return;
      const { chip, popup } = active;
      if (chip.matches(':hover') || popup.matches(':hover') || (document.hasFocus() && (chip.contains(document.activeElement) || popup.contains(document.activeElement)))) return;
      close();
    }, 180);
  }
  function place(chip: HTMLElement, popup: HTMLElement) {
    const anchor = chip.getBoundingClientRect(), rect = popup.getBoundingClientRect(), margin = 12, gap = 16;
    const left = Math.max(margin, Math.min(window.innerWidth - rect.width - margin, anchor.left + anchor.width / 2 - rect.width / 2));
    const proposedTop = anchor.top - rect.height - gap >= margin ? anchor.top - rect.height - gap : anchor.bottom + gap;
    const top = Math.max(margin, Math.min(window.innerHeight - rect.height - margin, proposedTop));
    popup.style.left = `${Math.round(left)}px`; popup.style.top = `${Math.round(top)}px`;
  }
  function open(chip: HTMLElement, focus = false) {
    cancelClose();
    const current = state(chip); if (!current) return;
    if (active?.chip === chip && active.signature === current.signature) { if (focus) active.popup.focus(); return; }
    close();
    const popup = document.createElement('section');
    popup.id = `reados-annotation-popover-${++sequence}`;
    popup.className = 'reados-annotation-popover'; popup.tabIndex = -1;
    popup.setAttribute('role', 'dialog'); popup.setAttribute('aria-label', current.referenceIndex ? `注释 ${current.referenceIndex}` : `${current.items.length} 条注释`);
    popup.style.setProperty('--annotation-accent', current.accent);
    for (const [index, item] of current.items.entries()) {
      const row = document.createElement('div'); row.className = 'reados-annotation-row';
      const number = document.createElement('span'); number.className = 'reados-annotation-number'; number.textContent = `${current.referenceIndex || index + 1}.`;
      const content = document.createElement('div'); content.className = 'reados-annotation-content';
      const label = document.createElement('div'); label.className = 'reados-annotation-label'; label.textContent = '所选文本：';
      const value = document.createElement('div'); value.className = 'reados-annotation-value'; value.textContent = item.text || item.selectedText;
      content.append(label, value);
      if (trimmed(item.annotation || item.comment)) {
        const commentLabel = document.createElement('div'); commentLabel.className = 'reados-annotation-label reados-annotation-comment-label'; commentLabel.textContent = '用户评论：';
        const comment = document.createElement('div'); comment.className = 'reados-annotation-value'; comment.textContent = item.annotation || item.comment;
        content.append(commentLabel, comment);
      }
      row.append(number, content); popup.append(row);
    }
    popup.addEventListener('mouseenter', cancelClose);
    popup.addEventListener('mouseleave', scheduleClose);
    popup.addEventListener('focusin', cancelClose);
    popup.addEventListener('focusout', scheduleClose);
    active = { chip, popup, signature: current.signature, annotationID: current.annotationID, messageID: current.messageID };
    chip.setAttribute('aria-expanded', 'true'); chip.setAttribute('aria-controls', popup.id);
    document.body.append(popup); place(chip, popup);
    if (current.annotationID) w.ReadexResponseAnnotationRenderer?.setActive?.(current.annotationID, current.messageID, true);
    if (focus) popup.focus();
  }
  function decorate(chip: HTMLElement) {
    const current = state(chip); if (!current) return;
    if (!current.referenceIndex) {
      const label = chip.querySelector('.selected-text-reference-label');
      const count = `${current.items.length} 条注释`;
      if (label && label.textContent !== count) label.textContent = count;
      chip.style.color = current.accent; chip.style.webkitTextFillColor = current.accent;
      chip.style.borderColor = current.border; chip.style.background = current.background; chip.style.cursor = 'pointer';
      // The generic text tooltip belongs to the attachment renderer. The native
      // adapter replaces it with the annotation list; Web follows the same path.
      chip.setAttribute('data-native-preview', 'true'); chip.setAttribute('role', 'button'); chip.tabIndex = 0;
      chip.setAttribute('aria-haspopup', 'dialog'); chip.setAttribute('aria-label', count);
    }
    if (active?.chip !== chip) chip.setAttribute('aria-expanded', 'false');
    if (bound.has(chip)) return; bound.add(chip);
    chip.addEventListener('mouseenter', () => open(chip));
    chip.addEventListener('mouseleave', scheduleClose);
    chip.addEventListener('focusin', () => { if (!restoringFocus) open(chip); });
    chip.addEventListener('focusout', scheduleClose);
    chip.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); open(chip); });
    chip.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(chip, true); }
    });
  }
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && active) { event.preventDefault(); event.stopPropagation(); close(true); }
  }, true);
  document.addEventListener('pointerdown', event => {
    if (active && event.target instanceof Node && !active.chip.contains(event.target) && !active.popup.contains(event.target)) close();
  }, true);
  window.addEventListener('resize', () => { if (active) place(active.chip, active.popup); });
  window.addEventListener('blur', () => close());
  document.addEventListener('visibilitychange', () => { if (document.visibilityState !== 'visible') close(); });
  document.addEventListener('scroll', event => { if (active && !active.popup.contains(event.target as Node)) place(active.chip, active.popup); }, true);
  w.__readosAnnotationCapsules = { openReference(payload: any) {
    if (typeof payload?.messageID !== 'string' || typeof payload?.annotationID !== 'string' || !Number.isSafeInteger(payload?.annotationIndex) || payload.annotationIndex < 1) return;
    const chip = [...document.querySelectorAll<HTMLElement>('.readex-response-annotation-reference')].find(element =>
      element.dataset.messageId === payload.messageID && element.dataset.readexAnnotationId === payload.annotationID
      && Number(element.dataset.readexAnnotationIndex) === payload.annotationIndex);
    if (chip) open(chip);
  }, refresh() {
    document.querySelectorAll<HTMLElement>('.selected-text-reference-chip, .readex-response-annotation-reference').forEach(decorate);
    if (!active) return;
    if (!active.chip.isConnected) { close(); return; }
    const current = state(active.chip);
    if (!current) { close(); return; }
    if (current.signature !== active.signature) open(active.chip);
  } };
}

export const ANNOTATION_CAPSULE_STYLE = `.reados-annotation-popover{position:fixed;z-index:2147483647;box-sizing:border-box;width:min(460px,calc(100vw - 24px));max-height:min(760px,calc(100vh - 24px));min-height:120px;overflow:auto;overscroll-behavior:contain;padding:20px;border-radius:18px;background:rgba(255,255,255,.98);box-shadow:0 8px 32px #0003,0 0 0 .5px #0002;color:var(--annotation-accent);font:600 17px/1.5 -apple-system,BlinkMacSystemFont,"SF Pro Text",sans-serif;text-align:left;user-select:text;-webkit-user-select:text}.reados-annotation-row{display:flex;align-items:flex-start;gap:8px;min-height:44px}.reados-annotation-row+.reados-annotation-row{margin-top:18px;padding-top:18px;border-top:.5px solid #8885}.reados-annotation-number{min-width:28px;flex-shrink:0}.reados-annotation-content{min-width:0;flex:1}.reados-annotation-label{color:color-mix(in srgb,var(--annotation-accent) 58%,transparent);margin-bottom:8px}.reados-annotation-value{white-space:pre-wrap;overflow-wrap:anywhere;color:color-mix(in srgb,var(--annotation-accent) 96%,transparent)}.reados-annotation-comment-label{margin-top:16px}.reados-annotation-popover:focus-visible{outline:2px solid color-mix(in srgb,var(--annotation-accent) 50%,transparent);outline-offset:-2px}.selected-text-reference-chip:focus-visible{outline:2px solid currentColor;outline-offset:3px}:root[data-theme="dark"] .reados-annotation-popover{background:rgba(36,36,38,.98);box-shadow:0 8px 32px #0007,0 0 0 .5px #fff3}`;
